import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useRevalidator } from 'react-router-dom';

import {
	MhicAssessmentModal,
	MhicCloseEpisodeModal,
	// MhicConsentModal,
	MhicContactInformationModal,
	MhicDemographicsModal,
	MhicEpisodeCard,
	MhicNextStepsAlerts,
	MhicNextStepsAppointment,
	MhicNextStepsResources,
	MhicScheduleAssessmentModal,
	MhicScheduleCallCompleteModal,
	MhicScheduleCallModal,
	MhicSchedulingDepartmentModal,
	MhicSelectAssessmentTypeModal,
	AssessmentTypeId,
	MhicTriageCard,
} from '@/components/integrated-care/mhic';
import NoData from '@/components/no-data';
import useFlags from '@/hooks/use-flags';
import useHandleError from '@/hooks/use-handle-error';
import {
	// PatientOrderConsentStatusId,
	PatientOrderDispositionId,
	PatientOrderIntakeScreeningStatusId,
	PatientOrderModel,
	PatientOrderScheduledOutreachReasonId,
	PatientOrderScheduledOutreach,
	PatientOrderScreeningStatusId,
	ScreeningSessionScreeningResult,
	AnalyticsNativeEventTypeId,
	ModifiedAssessmentTypeId,
} from '@/lib/models';
import { analyticsService, integratedCareService } from '@/lib/services';

import useAccount from '@/hooks/use-account';
import { useScreeningFlow } from '@/pages/screening/screening.hooks';
import { useIntegratedCareLoaderData } from '@/routes/ic/landing';
import { getDepartmentTriageDefaultLabel, shouldUseSchedulingWorkflow } from '@/lib/utils';
import { MhicVoicemailTaskModal } from './mhic-voicemail-task-modal';
import { MhicResetAssessmentModel } from '@/components/integrated-care/mhic/mhic-reset-assessment-modal';
import SvgIcon from '@/components/svg-icon';

interface Props {
	patientOrder: PatientOrderModel;
	pastPatientOrders: PatientOrderModel[];
}

enum ASSESSMENT_STATUS {
	NO_ASSESSMENT = 'NO_ASSESSMENT',
	SCHEDULED = 'SCHEDULED',
	IN_PROGRESS = 'IN_PROGRESS',
	COMPLETE = 'COMPLETE',
}

export const MhicOrderDetails = ({ patientOrder, pastPatientOrders }: Props) => {
	const { account, institution } = useAccount();
	const { referenceDataResponse } = useIntegratedCareLoaderData();
	const handleError = useHandleError();
	const { addFlag } = useFlags();
	const navigate = useNavigate();
	const revalidator = useRevalidator();

	const [showScheduleAssessmentModal, setShowScheduleAssessmentModal] = useState(false);
	const [showDemographicsModal, setShowDemographicsModal] = useState(false);
	const [showContactInformationModal, setShowContactInformationModal] = useState(false);
	const [showCloseEpisodeModal, setShowCloseEpisodeModal] = useState(false);
	const [showAddVoicemailTaskModal, setShowAddVoicemailTaskModal] = useState(false);
	const [showSelectAssessmentTypeModal, setShowSelectAssessmentTypeModal] = useState(false);
	const [showScheduleCallEditModal, setShowScheduleCallEditModal] = useState(false);
	const [showScheduleCallCompleteModal, setShowScheduleCallCompleteModal] = useState(false);
	const [scheduledOutreachToEdit, setScheduledOutreachToEdit] = useState<PatientOrderScheduledOutreach>();
	const [showResetModel, setShowResetModel] = useState(false);
	const [showSchedulingDepartmentModal, setShowSchedulingDepartmentModal] = useState(false);
	const [schedulingEpicDepartmentName, setSchedulingEpicDepartmentName] = useState<string>(
		patientOrder.overrideSchedulingEpicDepartmentId ?? ''
	);
	const defaultDepartmentTriageLabel = getDepartmentTriageDefaultLabel(patientOrder);
	const shouldShowAppointmentSection = shouldUseSchedulingWorkflow(patientOrder);
	const departmentTriageDisplayLabel = patientOrder.overrideSchedulingEpicDepartmentId
		? schedulingEpicDepartmentName
		: defaultDepartmentTriageLabel;

	const [screeningSessionScreeningResult, setScreeningSessionScreeningResult] =
		useState<ScreeningSessionScreeningResult>();
	// const [showConsentModal, setShowConsentModal] = useState(false);
	const hasCompletedIntakeScreening =
		patientOrder?.patientOrderIntakeScreeningStatusId === PatientOrderIntakeScreeningStatusId.COMPLETE;
	const { renderedPreScreeningLoader: loadingIntakeScreening, ...intakeScreeningFlow } = useScreeningFlow({
		screeningFlowId: institution?.integratedCareIntakeScreeningFlowId,
		patientOrderId: patientOrder.patientOrderId,
		instantiateOnLoad: false,
	});
	const { renderedPreScreeningLoader: loadingClinicalScreening, ...clinicalScreeningFlow } = useScreeningFlow({
		screeningFlowId: institution?.integratedCareScreeningFlowId,
		patientOrderId: patientOrder.patientOrderId,
		instantiateOnLoad: false,
		disabled: !hasCompletedIntakeScreening,
	});

	const handleCloseEpisodeModalSave = useCallback(
		async (patientOrderClosureReasonId: string) => {
			try {
				await integratedCareService
					.closePatientOrder(patientOrder.patientOrderId, { patientOrderClosureReasonId })
					.fetch();

				setShowCloseEpisodeModal(false);
				revalidator.revalidate();
				addFlag({
					variant: 'success',
					title: 'Episode Closed',
					actions: [],
				});
			} catch (error) {
				handleError(error);
			}
		},
		[addFlag, handleError, patientOrder.patientOrderId, revalidator]
	);

	const incompleteVoicemailTask = useMemo(() => {
		return patientOrder.patientOrderVoicemailTasks.find((vmt) => !vmt.completed);
	}, [patientOrder.patientOrderVoicemailTasks]);

	const navigateToAssessment = useCallback(
		(options: {
			createNew?: boolean;
			resumeRecent?: boolean;
			modifiedAssessmentType?: ModifiedAssessmentTypeId;
		}) => {
			// if (patientOrder.patientOrderConsentStatusId === PatientOrderConsentStatusId.UNKNOWN) {
			// 	setShowConsentModal(true);
			// } else {
			if (options.createNew) {
				const hasModifiedAssessment = Boolean(options.modifiedAssessmentType);
				intakeScreeningFlow.createScreeningSession(hasModifiedAssessment, options.modifiedAssessmentType);
			} else if (options.resumeRecent) {
				if (!hasCompletedIntakeScreening) {
					intakeScreeningFlow.resumeScreeningSession(patientOrder.mostRecentIntakeScreeningSessionId);
				} else {
					clinicalScreeningFlow.resumeScreeningSession(patientOrder.mostRecentScreeningSessionId);
				}
			}
			// }
		},
		[
			clinicalScreeningFlow,
			hasCompletedIntakeScreening,
			intakeScreeningFlow,
			patientOrder.mostRecentIntakeScreeningSessionId,
			patientOrder.mostRecentScreeningSessionId,
		]
	);

	const isCreatingScreeningSession =
		intakeScreeningFlow.isCreatingScreeningSession || clinicalScreeningFlow.isCreatingScreeningSession;

	const [assessmentStatus, setAssessmentStatus] = useState<ASSESSMENT_STATUS>(ASSESSMENT_STATUS.NO_ASSESSMENT);

	useEffect(() => {
		if (patientOrder.mostRecentIntakeAndClinicalScreeningsSatisfied) {
			setAssessmentStatus(ASSESSMENT_STATUS.COMPLETE);
			return;
		}

		if (patientOrder.patientOrderScreeningStatusId === PatientOrderScreeningStatusId.SCHEDULED) {
			setAssessmentStatus(ASSESSMENT_STATUS.SCHEDULED);
			return;
		}

		if (
			patientOrder.patientOrderIntakeScreeningStatusId === PatientOrderIntakeScreeningStatusId.IN_PROGRESS ||
			patientOrder.patientOrderScreeningStatusId === PatientOrderScreeningStatusId.IN_PROGRESS
		) {
			setAssessmentStatus(ASSESSMENT_STATUS.IN_PROGRESS);
			return;
		}

		setAssessmentStatus(ASSESSMENT_STATUS.NO_ASSESSMENT);
	}, [
		patientOrder.mostRecentIntakeAndClinicalScreeningsSatisfied,
		patientOrder.patientOrderIntakeScreeningStatusId,
		patientOrder.patientOrderScreeningStatusId,
	]);

	const inProgressAssessmentCreatedByAccountDisplayName =
		patientOrder.patientOrderIntakeScreeningStatusId === PatientOrderIntakeScreeningStatusId.IN_PROGRESS
			? patientOrder.mostRecentIntakeScreeningSessionCreatedByAccountDisplayName
			: patientOrder.mostRecentScreeningSessionCreatedByAccountDisplayName;
	const inProgressAssessmentCreatedAtDescription =
		patientOrder.patientOrderIntakeScreeningStatusId === PatientOrderIntakeScreeningStatusId.IN_PROGRESS
			? patientOrder.mostRecentIntakeScreeningSessionCreatedAtDescription
			: patientOrder.mostRecentScreeningSessionCreatedAtDescription;
	const inProgressAssessmentDescription =
		inProgressAssessmentCreatedByAccountDisplayName && inProgressAssessmentCreatedAtDescription
			? `${inProgressAssessmentCreatedByAccountDisplayName} began the assessment on ${inProgressAssessmentCreatedAtDescription}`
			: undefined;

	const handleResetPatientOrder = useCallback(async () => {
		try {
			await integratedCareService.resetPatientOrder(patientOrder.patientOrderId).fetch();

			revalidator.revalidate();
			setShowResetModel(false);
			addFlag({
				variant: 'success',
				title: 'Success',
				description: 'Patient order was reset.',
				actions: [],
			});

			analyticsService.persistEvent(AnalyticsNativeEventTypeId.CLICKTHROUGH_MHIC_RETAKE_ORDER_ASSESSMENT, {
				patientOrderId: patientOrder.patientOrderId,
			});
		} catch (error) {
			handleError(error);
		}
	}, [addFlag, handleError, patientOrder.patientOrderId, revalidator]);

	const fetchAndSetSchedulingEpicDepartmentName = useCallback(async () => {
		if (!patientOrder.overrideSchedulingEpicDepartmentId) {
			setSchedulingEpicDepartmentName('');
			return;
		}

		try {
			const { epicDepartments } = await integratedCareService.getEpicDepartments().fetch();
			const epicDepartment = epicDepartments.find(
				(i) => i.epicDepartmentId === patientOrder.overrideSchedulingEpicDepartmentId
			);

			setSchedulingEpicDepartmentName(
				epicDepartment
					? `${epicDepartment.name} (${epicDepartment.departmentId})`
					: patientOrder.overrideSchedulingEpicDepartmentId ?? ''
			);
		} catch (error) {
			handleError(error);
		}
	}, [handleError, patientOrder.overrideSchedulingEpicDepartmentId]);

	useEffect(() => {
		fetchAndSetSchedulingEpicDepartmentName();
	}, [fetchAndSetSchedulingEpicDepartmentName]);

	if (loadingIntakeScreening || loadingClinicalScreening) {
		return loadingIntakeScreening || loadingClinicalScreening;
	}

	return (
		<>
			<MhicScheduleAssessmentModal
				patientOrder={patientOrder}
				show={showScheduleAssessmentModal}
				onHide={() => {
					setShowScheduleAssessmentModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowScheduleAssessmentModal(false);
				}}
			/>

			{/* <MhicConsentModal
				patientOrder={patientOrder}
				show={showConsentModal}
				onHide={() => {
					setShowConsentModal(false);
				}}
				onSave={(updatedPatientOrder) => {
					if (updatedPatientOrder.patientOrderConsentStatusId === PatientOrderConsentStatusId.CONSENTED) {
						navigate(`/ic/mhic/order-assessment/${patientOrder.patientOrderId}`);
					} else {
						revalidator.revalidate();
						setShowConsentModal(false);
					}
				}}
			/> */}

			<MhicAssessmentModal
				show={!!screeningSessionScreeningResult}
				screeningType={referenceDataResponse.screeningTypes.find(
					(st) => st.screeningTypeId === screeningSessionScreeningResult?.screeningTypeId
				)}
				screeningSessionScreeningResult={screeningSessionScreeningResult}
				onHide={() => {
					setScreeningSessionScreeningResult(undefined);
				}}
			/>

			<MhicDemographicsModal
				raceOptions={referenceDataResponse.races}
				ethnicityOptions={referenceDataResponse.ethnicities}
				genderIdentityOptions={referenceDataResponse.genderIdentities}
				patientOrder={patientOrder}
				show={showDemographicsModal}
				onHide={() => {
					setShowDemographicsModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowDemographicsModal(false);
				}}
			/>

			<MhicContactInformationModal
				patientOrder={patientOrder}
				show={showContactInformationModal}
				onHide={() => {
					setShowContactInformationModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowContactInformationModal(false);
				}}
			/>

			<MhicCloseEpisodeModal
				show={showCloseEpisodeModal}
				onHide={() => {
					setShowCloseEpisodeModal(false);
				}}
				onSave={handleCloseEpisodeModalSave}
			/>

			<MhicVoicemailTaskModal
				patientOrderVoicemailTask={incompleteVoicemailTask}
				patientOrder={patientOrder}
				show={showAddVoicemailTaskModal}
				onHide={() => {
					setShowAddVoicemailTaskModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowAddVoicemailTaskModal(false);
				}}
			/>

			<MhicResetAssessmentModel
				show={showResetModel}
				onHide={() => {
					setShowResetModel(false);
				}}
				onReset={handleResetPatientOrder}
			/>

			<MhicSelectAssessmentTypeModal
				show={showSelectAssessmentTypeModal}
				onHide={() => {
					setShowSelectAssessmentTypeModal(false);
				}}
				onSave={(assessmentTypeId: AssessmentTypeId) => {
					const modifiedAssessmentType = assessmentTypeId === 'DEFAULT' ? undefined : assessmentTypeId;

					setShowSelectAssessmentTypeModal(false);
					navigateToAssessment({
						createNew: true,
						modifiedAssessmentType,
					});
				}}
			/>

			{scheduledOutreachToEdit && (
				<MhicScheduleCallModal
					show={showScheduleCallEditModal}
					patientOrderScheduledOutreach={scheduledOutreachToEdit}
					patientOrder={patientOrder}
					onHide={() => {
						setShowScheduleCallEditModal(false);
					}}
					onSave={() => {
						revalidator.revalidate();
						setShowScheduleCallEditModal(false);
					}}
				/>
			)}

			{scheduledOutreachToEdit && (
				<MhicScheduleCallCompleteModal
					show={showScheduleCallCompleteModal}
					patientOrderScheduledOutreachId={scheduledOutreachToEdit.patientOrderScheduledOutreachId}
					patientOrder={patientOrder}
					onHide={() => {
						setShowScheduleCallCompleteModal(false);
					}}
					onSave={() => {
						revalidator.revalidate();
						setShowScheduleCallCompleteModal(false);
					}}
				/>
			)}

			<MhicSchedulingDepartmentModal
				show={showSchedulingDepartmentModal}
				patientOrder={patientOrder}
				onHide={() => {
					setShowSchedulingDepartmentModal(false);
				}}
				onSave={() => {
					revalidator.revalidate();
					setShowSchedulingDepartmentModal(false);
				}}
			/>

			{patientOrder.patientOrderScheduledOutreaches.length > 0 && (
				<section>
					<Container fluid>
						{patientOrder.patientOrderScheduledOutreaches.map(
							(scheduledOutreach, scheduledOutreachIndex) => {
								const isLast =
									patientOrder.patientOrderScheduledOutreaches.length - 1 === scheduledOutreachIndex;

								return (
									<Row
										key={scheduledOutreach.patientOrderScheduledOutreachId}
										className={classNames({ 'mb-6': !isLast })}
									>
										<Col>
											<Card bsPrefix="ic-card">
												<Card.Header className="border-0">
													<Card.Title>Phone call scheduled</Card.Title>
													<div className="button-container">
														<Button
															variant="light"
															size="sm"
															onClick={() => {
																setScheduledOutreachToEdit(scheduledOutreach);
																setShowScheduleCallCompleteModal(true);
															}}
														>
															Mark Complete
														</Button>
														<Button
															variant="light"
															className="ms-2 p-2"
															onClick={() => {
																setScheduledOutreachToEdit(scheduledOutreach);
																setShowScheduleCallEditModal(true);
															}}
														>
															<SvgIcon
																kit="far"
																icon="pen"
																size={16}
																className="d-flex"
															/>
														</Button>
													</div>
												</Card.Header>
												<Card.Body>
													<Container fluid>
														<Row className="mb-4">
															<Col xs={3}>
																<p className="m-0 text-gray">Date &amp; Time</p>
															</Col>
															<Col xs={9}>
																<p className="m-0">
																	{scheduledOutreach.scheduledAtDateTimeDescription}
																</p>
															</Col>
														</Row>
														<Row className="mb-4">
															<Col xs={3}>
																<p className="m-0 text-gray">Call Type</p>
															</Col>
															<Col xs={9}>
																<p className="m-0">
																	{scheduledOutreach.patientOrderScheduledOutreachReasonId ===
																		PatientOrderScheduledOutreachReasonId.RESOURCE_FOLLOWUP &&
																		'Resource Followup'}
																	{scheduledOutreach.patientOrderScheduledOutreachReasonId ===
																		PatientOrderScheduledOutreachReasonId.OTHER &&
																		'Other'}
																</p>
															</Col>
														</Row>
														<Row>
															<Col xs={3}>
																<p className="m-0 text-gray">Notes</p>
															</Col>
															<Col xs={9}>
																<p className="m-0">{scheduledOutreach.message}</p>
															</Col>
														</Row>
													</Container>
												</Card.Body>
											</Card>
										</Col>
									</Row>
								);
							}
						)}
					</Container>
				</section>
			)}

			{incompleteVoicemailTask && (
				<section>
					<Container fluid>
						<Row>
							<Col>
								<Card bsPrefix="ic-card">
									<Card.Header>
										<Card.Title>Voicemail Task</Card.Title>
										<div className="button-container">
											<Button
												variant="light"
												size="sm"
												onClick={async () => {
													await integratedCareService
														.completeVoicemailTask(
															incompleteVoicemailTask.patientOrderVoicemailTaskId
														)
														.fetch();

													revalidator.revalidate();
												}}
											>
												Mark Complete
											</Button>
											<Button
												variant="light"
												className="ms-2 p-2"
												onClick={() => {
													setShowAddVoicemailTaskModal(true);
												}}
											>
												<SvgIcon kit="far" icon="pen" size={16} className="d-flex" />
											</Button>
										</div>
									</Card.Header>
									<Card.Body>
										<Container fluid>
											<Row className="mb-4">
												<Col xs={3}>
													<p className="m-0 text-gray">Created At</p>
												</Col>
												<Col xs={9}>
													<p className="m-0">{incompleteVoicemailTask.createdDescription}</p>
												</Col>
											</Row>
											<Row>
												<Col xs={3}>
													<p className="m-0 text-gray">Notes</p>
												</Col>
												<Col xs={9}>
													<p className="m-0">{incompleteVoicemailTask.message}</p>
												</Col>
											</Row>
										</Container>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</Container>
				</section>
			)}

			<section>
				<Container fluid>
					<Row>
						<Col>
							<MhicEpisodeCard patientOrder={patientOrder} />
						</Col>
					</Row>
				</Container>
			</section>
			<section>
				<Container fluid>
					{assessmentStatus === ASSESSMENT_STATUS.COMPLETE && (
						<>
							<Row className="mb-6">
								<Col>
									<div className="d-flex align-items-center justify-content-between">
										<h4 className="mb-0">Assessment</h4>
										<div className="d-flex align-items-center">
											<Button
												variant="link"
												className="text-decoration-none d-flex align-items-center"
												onClick={() => {
													setShowResetModel(true);
												}}
											>
												<SvgIcon
													kit="far"
													icon="arrow-rotate-left"
													size={16}
													className="me-2"
												/>
												Reset
											</Button>
											<Button
												variant="primary"
												className="d-flex align-items-center"
												onClick={() => {
													analyticsService.persistEvent(
														AnalyticsNativeEventTypeId.CLICKTHROUGH_MHIC_ORDER_ASSESSMENT_RESULTS,
														{
															patientOrderId: patientOrder.patientOrderId,
														}
													);

													navigate(
														`/ic/mhic/order-assessment/${patientOrder.patientOrderId}`
													);
												}}
											>
												Review{' '}
												<SvgIcon
													kit="far"
													icon="arrow-up-right-from-square"
													size={16}
													className="ms-2"
												/>
											</Button>
										</div>
									</div>
									<p className="mb-0">
										Completed{' '}
										<strong>
											{patientOrder.screeningSession?.completedAtDescription ??
												patientOrder.intakeScreeningSession?.completedAtDescription}
										</strong>{' '}
										{patientOrder.mostRecentScreeningSessionCreatedByAccountDisplayName && (
											<span>by </span>
										)}
										<strong>
											{patientOrder.mostRecentScreeningSessionCreatedByAccountDisplayName ??
												patientOrder.mostRecentIntakeScreeningSessionCreatedByAccountDisplayName}
										</strong>
									</p>
								</Col>
							</Row>

							<MhicNextStepsAlerts
								patientOrder={patientOrder}
								referenceData={referenceDataResponse}
								disabled={patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN}
							/>

							<MhicTriageCard
								patientOrder={patientOrder}
								disabled={patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN}
								className="mb-6"
							/>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Department Triage</Card.Title>
									<div className="button-container">
										<Button
											variant="light"
											size="sm"
											onClick={() => {
												setShowSchedulingDepartmentModal(true);
											}}
											disabled={
												patientOrder.patientOrderDispositionId !==
													PatientOrderDispositionId.OPEN ||
												!account?.accountCapabilityFlags.canEditIcTriages
											}
										>
											Override
										</Button>
									</div>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row>
											<Col xs={3}>
												<p className="m-0 text-gray">Department</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">{departmentTriageDisplayLabel}</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</>
					)}
					{assessmentStatus === ASSESSMENT_STATUS.NO_ASSESSMENT && (
						<>
							<Row className="mb-6">
								<Col>
									<h4 className="mb-0">Assessment</h4>
								</Col>
							</Row>
							<Row>
								<Col>
									<MhicNextStepsAlerts
										patientOrder={patientOrder}
										referenceData={referenceDataResponse}
										disabled={
											patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN
										}
									/>
									<NoData
										title="No Assessment"
										description="There is no assessment for the patient's most recent referral order"
										actions={[
											{
												variant: 'primary',
												title: 'Start Assessment',
												onClick: () => {
													setShowSelectAssessmentTypeModal(true);
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
														PatientOrderDispositionId.OPEN || isCreatingScreeningSession,
											},
											{
												variant: 'outline-primary',
												title: 'Schedule Assessment',
												onClick: () => {
													setShowScheduleAssessmentModal(true);
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
													PatientOrderDispositionId.OPEN,
											},
										]}
									/>
								</Col>
							</Row>
						</>
					)}
					{assessmentStatus === ASSESSMENT_STATUS.SCHEDULED && (
						<>
							<Row className="mb-6">
								<Col>
									<h4 className="mb-0">Assessment</h4>
								</Col>
							</Row>
							<Row>
								<Col>
									<MhicNextStepsAlerts
										patientOrder={patientOrder}
										referenceData={referenceDataResponse}
										disabled={
											patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN
										}
									/>
									<NoData
										className="bg-white"
										title="Assessment is Scheduled"
										description={
											patientOrder.patientOrderScheduledScreeningScheduledDateTimeDescription
										}
										actions={[
											{
												variant: 'primary',
												title: 'Start Assessment',
												onClick: () => {
													setShowSelectAssessmentTypeModal(true);
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
														PatientOrderDispositionId.OPEN || isCreatingScreeningSession,
											},
											{
												variant: 'outline-primary',
												title: 'Edit Appointment Date',
												onClick: () => {
													setShowScheduleAssessmentModal(true);
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
													PatientOrderDispositionId.OPEN,
											},
										]}
									/>
								</Col>
							</Row>
						</>
					)}
					{assessmentStatus === ASSESSMENT_STATUS.IN_PROGRESS && (
						<>
							<Row className="mb-6">
								<Col>
									<h4 className="mb-0">Assessment</h4>
								</Col>
							</Row>
							<Row>
								<Col>
									<MhicNextStepsAlerts
										patientOrder={patientOrder}
										referenceData={referenceDataResponse}
										disabled={
											patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN
										}
									/>
									<NoData
										className="bg-white"
										title="Assessment in Progress"
										description={inProgressAssessmentDescription}
										actions={[
											{
												variant: 'primary',
												title: 'Continue Assessment',
												onClick: () => {
													navigateToAssessment({
														resumeRecent: true,
													});
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
													PatientOrderDispositionId.OPEN,
											},
											{
												variant: 'outline-primary',
												title: 'Review',
												icon: (
													<SvgIcon
														kit="far"
														icon="arrow-up-right-from-square"
														size={16}
														className="ms-2 order-last"
													/>
												),
												onClick: () => {
													analyticsService.persistEvent(
														AnalyticsNativeEventTypeId.CLICKTHROUGH_MHIC_ORDER_ASSESSMENT_RESULTS,
														{
															patientOrderId: patientOrder.patientOrderId,
														}
													);

													navigate(
														`/ic/mhic/order-assessment/${patientOrder.patientOrderId}`
													);
												},
											},
											{
												variant: 'link',
												icon: (
													<SvgIcon
														kit="far"
														icon="arrow-rotate-left"
														size={16}
														className="me-2"
													/>
												),
												className: 'text-decoration-none',
												title: 'Reset',
												onClick: () => {
													setShowResetModel(true);
												},
												disabled:
													patientOrder.patientOrderDispositionId !==
														PatientOrderDispositionId.OPEN || isCreatingScreeningSession,
											},
										]}
									/>
								</Col>
							</Row>
						</>
					)}
				</Container>
			</section>
			{shouldShowAppointmentSection && (
				<section>
					<Container fluid>
						<Row className="mb-6">
							<Col>
								<h4 className="mb-0">Appointment</h4>
							</Col>
						</Row>
						<MhicNextStepsAppointment
							patientOrder={patientOrder}
							disabled={patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN}
						/>
					</Container>
				</section>
			)}
			{assessmentStatus === ASSESSMENT_STATUS.COMPLETE && (
				<section>
					<Container fluid>
						<Row className="mb-6">
							<Col>
								<h4 className="mb-0">Resources</h4>
							</Col>
						</Row>
						<MhicNextStepsResources
							patientOrder={patientOrder}
							referenceData={referenceDataResponse}
							disabled={patientOrder.patientOrderDispositionId !== PatientOrderDispositionId.OPEN}
						/>
					</Container>
				</section>
			)}
			<section>
				<Container fluid>
					<Row className="mb-6">
						<Col>
							<h4 className="mb-0">Contact</h4>
						</Col>
					</Row>
					<Row>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Primary Contact</Card.Title>
									<div className="button-container">
										<Button
											variant="transparent-secondary"
											className="p-2"
											onClick={() => {
												setShowContactInformationModal(true);
											}}
											disabled={
												patientOrder.patientOrderDispositionId !==
												PatientOrderDispositionId.OPEN
											}
										>
											<SvgIcon kit="far" icon="pen" size={16} className="d-flex" />
										</Button>
									</div>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col xs={3}>
												<p className="m-0 text-gray">Phone Number</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">
													{patientOrder.patientPhoneNumberDescription ?? 'Not Available'}
												</p>
											</Col>
										</Row>
										<Row>
											<Col xs={3}>
												<p className="m-0 text-gray">Email</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">
													{patientOrder.patientEmailAddress ?? 'Not Available'}
												</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					{/* <Row>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>
										Patient's Father <span className="text-danger">[TODO]: Emergency Contact?</span>
									</Card.Title>
									<div className="button-container">
										<Button variant="light" className="p-2">
											<EditIcon className="d-flex" />
										</Button>
									</div>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row>
											<Col xs={3}>
												<p className="m-0 text-gray">Mobile Phone</p>
											</Col>
											<Col xs={9}>
												<div className="d-flex align-items-center">
													<p className="m-0 text-gray">(000) 000-0000</p>
													<Badge bg="outline-primary" pill className="ms-2">
														Preferred
													</Badge>
												</div>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row> */}
				</Container>
			</section>
			<section>
				<Container fluid>
					<Row className="mb-6">
						<Col>
							<h4 className="mb-0">Patient Information</h4>
						</Col>
					</Row>
					<Row className="mb-6">
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Basic Info</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Date of birth</p>
											</Col>
											<Col>
												<p className="m-0">
													{patientOrder.patientBirthdateDescription ?? 'Unknown'}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Age</p>
											</Col>
											<Col>
												<p className="m-0 text danger">
													{patientOrder.patientAgeOnOrderDateDescription}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">City</p>
											</Col>
											<Col>
												<p className="m-0 text danger">
													{patientOrder.patientAddress?.locality}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">State</p>
											</Col>
											<Col>
												<p className="m-0 text danger">{patientOrder.patientAddress?.region}</p>
											</Col>
										</Row>
										<Row>
											<Col>
												<p className="m-0 text-gray">State</p>
											</Col>
											<Col>
												<p className="m-0 text danger">
													{patientOrder.patientAddress?.postalCode}
												</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Demographics</Card.Title>
									<div className="button-container">
										<Button
											variant="transparent-secondary"
											className="p-2"
											onClick={() => {
												setShowDemographicsModal(true);
											}}
											disabled={
												patientOrder.patientOrderDispositionId !==
												PatientOrderDispositionId.OPEN
											}
										>
											<SvgIcon kit="far" icon="pen" size={16} className="d-flex" />
										</Button>
									</div>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Pref. Language</p>
											</Col>
											<Col>
												<p className="m-0">
													{
														referenceDataResponse.languages.find(
															(language) =>
																language.languageCode ===
																patientOrder.patientLanguageCode
														)?.description
													}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Race</p>
											</Col>
											<Col>
												<p className="m-0">
													{referenceDataResponse.races.find(
														(race) => race.raceId === patientOrder.patientRaceId
													)?.description ?? 'Not Specified'}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Ethnicity</p>
											</Col>
											<Col>
												<p className="m-0">
													{referenceDataResponse.ethnicities.find(
														(ethnicity) =>
															ethnicity.ethnicityId === patientOrder.patientEthnicityId
													)?.description ?? 'Not Specified'}
												</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Birth Sex</p>
											</Col>
											<Col>
												<p className="m-0">
													{referenceDataResponse.birthSexes.find(
														(birthSex) =>
															birthSex.birthSexId === patientOrder.patientBirthSexId
													)?.description ?? 'Not Specified'}
												</p>
											</Col>
										</Row>
										<Row>
											<Col>
												<p className="m-0 text-gray">Gender Identity</p>
											</Col>
											<Col>
												<p className="m-0">
													{referenceDataResponse.genderIdentities.find(
														(genderIdentity) =>
															genderIdentity.genderIdentityId ===
															patientOrder.patientGenderIdentityId
													)?.description ?? 'Not Specified'}
												</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Insurance</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col>
												<p className="m-0">{patientOrder.primaryPayorName}</p>
											</Col>
										</Row>
										<Row>
											<Col>
												<p className="m-0">Plan: {patientOrder.primaryPlanName}</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Care Preferences</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col>
												<p className="m-0 text-gray">Appointment Type</p>
											</Col>
											<Col>
												<p className="m-0">
													{referenceDataResponse.patientOrderCarePreferences.find(
														(c) =>
															c.patientOrderCarePreferenceId ===
															patientOrder.patientOrderCarePreferenceId
													)?.description ?? 'N/A'}
												</p>
											</Col>
										</Row>
										<Row>
											<Col>
												<p className="m-0 text-gray">Travel Radius</p>
											</Col>
											<Col>
												<p className="m-0">
													{patientOrder.inPersonCareRadiusWithDistanceUnitDescription ??
														'N/A'}
												</p>
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				</Container>
			</section>
			<section>
				<Container fluid>
					<Row className="mb-6">
						<Col>
							<h4 className="mb-0">Clinical</h4>
						</Col>
					</Row>
					<Row className="mb-6">
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Comment</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row>
											<Col>
												{patientOrder.comments && (
													<div
														dangerouslySetInnerHTML={{
															__html: `<p class="mb-0">${patientOrder.comments?.replace(
																/\n/g,
																'<br/><br/>'
															)}</p>`,
														}}
													/>
												)}
												{!patientOrder.comments && <div>N/A</div>}
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					<Row className="mb-6">
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Diagnoses</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row>
											<Col>
												<p
													className={classNames({
														'mb-0': (patientOrder.patientOrderDiagnoses ?? []).length <= 0,
														'mb-2': (patientOrder.patientOrderDiagnoses ?? []).length > 0,
													})}
												>
													{patientOrder.associatedDiagnosis ?? 'N/A'}
												</p>
												{(patientOrder.patientOrderDiagnoses ?? []).length > 0 && (
													<ul className="mb-0">
														{patientOrder.patientOrderDiagnoses?.map(
															(patientOrderDiagnoses) => {
																return (
																	<li
																		key={
																			patientOrderDiagnoses.patientOrderDiagnosisId
																		}
																		className="m-0"
																	>
																		<strong>
																			{patientOrderDiagnoses.diagnosisId}
																		</strong>{' '}
																		{patientOrderDiagnoses.diagnosisName}
																	</li>
																);
															}
														)}
													</ul>
												)}
											</Col>
										</Row>
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
					{(patientOrder.patientOrderMedications ?? []).length > 0 && (
						<Row className="mb-6">
							<Col>
								<Card bsPrefix="ic-card">
									<Card.Header>
										<Card.Title>Medications</Card.Title>
									</Card.Header>
									<Card.Body>
										<Container fluid>
											<Row>
												<Col>
													{patientOrder.patientOrderMedications?.map(
														(patientOrderMedication) => {
															return (
																<p
																	key={
																		patientOrderMedication.patientOrderMedicationId
																	}
																	className="m-0"
																>
																	{patientOrderMedication.medicationName}
																</p>
															);
														}
													)}
												</Col>
											</Row>
										</Container>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					)}
					<Row>
						<Col>
							<Card bsPrefix="ic-card">
								<Card.Header>
									<Card.Title>Care Team</Card.Title>
								</Card.Header>
								<Card.Body>
									<Container fluid>
										<Row className="mb-4">
											<Col xs={3}>
												<p className="m-0 text-gray">Practice</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">{patientOrder.referringPracticeName ?? 'Unknown'}</p>
											</Col>
										</Row>
										<Row className="mb-4">
											<Col xs={3}>
												<p className="m-0 text-gray">PC Provider</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">
													{patientOrder.orderingProviderDisplayName ?? 'Unknown'}
												</p>
											</Col>
										</Row>
										<hr className="mb-4" />
										<Row className="mb-4">
											<Col xs={3}>
												<p className="m-0 text-gray">MHIC</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">
													{patientOrder.panelAccountDisplayName ?? 'Unassigned'}
												</p>
											</Col>
										</Row>
										<Row>
											<Col xs={3}>
												<p className="m-0 text-gray">MHP</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">{patientOrder.providerName ?? 'N/A'}</p>
											</Col>
										</Row>
										{/* <Row>
											<Col xs={3}>
												<p className="m-0 text-gray">Psychiatrist</p>
											</Col>
											<Col xs={9}>
												<p className="m-0">
													<span className="text-danger">[TODO]</span>
												</p>
											</Col>
										</Row> */}
									</Container>
								</Card.Body>
							</Card>
						</Col>
					</Row>
				</Container>
			</section>
			<section>
				<Container fluid>
					<Row className="mb-6">
						<Col>
							<h4 className="mb-0">
								Episodes <span className="text-gray">({pastPatientOrders.length})</span>
							</h4>
						</Col>
					</Row>
					{pastPatientOrders.length > 0 && (
						<Row>
							<Col>
								<Card bsPrefix="ic-card">
									<Card.Header>
										<Card.Title>Past</Card.Title>
									</Card.Header>
									<Card.Body>
										<Container fluid>
											{pastPatientOrders.map((pastPatientOrder, pastPatientOrderIndex) => {
												const isLast = pastPatientOrderIndex === pastPatientOrders.length - 1;

												return (
													<Row
														key={pastPatientOrder.orderId}
														className={classNames({ 'mb-4 pb-6 border-bottom': !isLast })}
													>
														<Col>
															<div className="mb-1 d-flex align-items-center justify-content-between">
																<p className="m-0 fw-bold">
																	{pastPatientOrder.orderDateDescription} -{' '}
																	{patientOrder.episodeClosedAtDescription} (
																	{pastPatientOrder.episodeDurationInDaysDescription})
																</p>
																<p className="m-0 fw-bold text-gray">
																	{
																		pastPatientOrder.patientOrderDispositionDescription
																	}
																</p>
															</div>
														</Col>
													</Row>
												);
											})}
										</Container>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					)}
				</Container>
			</section>
		</>
	);
};
